import { useState, useEffect, useRef } from 'react'
import { Controller, useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAction, useConvex, useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingOverlay } from '@/components/ui/loading'
import { Odontogram } from './Odontogram'
import {
  MixedPrescriptionRows,
  emptyMixedRow,
  type MixedRowState,
} from './MixedPrescriptionRows'
import { formatCurrency } from '@/lib/utils'
import { Id } from '../../../convex/_generated/dataModel'
import { resolveServicePricingMode } from '../../../convex/lib/applicationType'
import { AlertCircle, AlertTriangle, Check, ChevronLeft, ChevronRight, Paperclip, X } from 'lucide-react'
import { getClinicalSelectionStatus } from './clinicalSelectionValidation'
import { prescriptionServiceQueryArgs } from './serviceQueryArgs'
import {
    createPrescriptionSchema,
    type PrescriptionFormInput,
    type PrescriptionFormData,
} from './prescriptionSchema'
import {
    laboratoryTestLabel,
    parseLaboratoryTests,
} from '../../../convex/lib/laboratoryTests'
import {
    getPrimoUpAttachmentValidationError,
    PRIMOUP_ATTACHMENT_ACCEPT,
} from '../../../shared/primoUpIntegration'

interface PrescriptionFormProps {
    onSuccess?: (prescriptionId: Id<'prescriptions'>) => void
    onCancel?: () => void
}

function getReadablePrescriptionError(error: unknown, fallback: string): string {
    if (!(error instanceof Error)) return fallback

    const rawMessage = error.message.trim()
    const convexMessage = rawMessage.match(/Uncaught Error:\s*([^\n]+)/)?.[1]
    const candidate = (convexMessage ?? rawMessage)
        .replace(/^\[CONVEX[^\]]*\]\s*/i, '')
        .replace(/^Server Error\s*/i, '')
        .replace(/^Error:\s*/i, '')
        .replace(/\s+at\s+handler.*$/i, '')
        .trim()

    if (
        !candidate ||
        candidate.length > 260 ||
        /failed to fetch|network error|server error|request id/i.test(candidate)
    ) {
        return fallback
    }

    return `${candidate.replace(/[.!?]+$/, '')}. Correggi i dati indicati e riprova.`
}

export function PrescriptionForm({ onSuccess, onCancel }: PrescriptionFormProps) {
    const { profile } = useAuth()
    const { hasPermission } = usePermissions()
    const convex = useConvex()
    const [selectedTeeth, setSelectedTeeth] = useState<string[]>([])
    const [selectedImplants, setSelectedImplants] = useState<string[]>([])
    const [implantTypes, setImplantTypes] = useState<Record<string, string>>({})
    const [calculatedPrice, setCalculatedPrice] = useState<number>(0)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [uploadingFiles, setUploadingFiles] = useState(false)
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
    const [selectedDoctorId, setSelectedDoctorId] = useState<string>('')
    const [isIntentionalSubmit, setIsIntentionalSubmit] = useState(false)
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
    const [prescriptionMode, setPrescriptionMode] = useState<'single' | 'mixed'>('single')
    const [mixedRows, setMixedRows] = useState<MixedRowState[]>([
      emptyMixedRow(),
      emptyMixedRow(),
    ])
    const [mixedSubmitting, setMixedSubmitting] = useState(false)
    // Intersezione dei laboratory_tests dei listini scelti nelle righe miste.
    // In modalità "mista" Tipo Prova è condivisa: mostriamo solo i tipi prova
    // disponibili in TUTTI i componenti, e nel submit risolviamo prova_id per
    // ogni listino con il match by-name.
    const [sharedTests, setSharedTests] = useState<Array<{ name: string; display_name?: string }>>([])
    const [sharedTestsLoading, setSharedTestsLoading] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [flowError, setFlowError] = useState<{
        step: number
        title: string
        description: string
    } | null>(null)
    const stepHeadingRef = useRef<HTMLHeadingElement>(null)
    const previousStepRef = useRef(currentStep)

    const focusElement = (id: string) => {
        requestAnimationFrame(() => {
            const element = document.getElementById(id)
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            element?.focus()
        })
    }

    const showFlowError = (
        step: number,
        title: string,
        description: string,
        targetId = 'prescription-flow-error'
    ) => {
        setCurrentStep(step)
        setFlowError({ step, title, description })
        focusElement(targetId)
    }

    const [companySearchTerm, setCompanySearchTerm] = useState('')
    const [doctorSearchTerm, setDoctorSearchTerm] = useState('')
    const [serviceSearchTerm, setServiceSearchTerm] = useState('')
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false)
    const [showDoctorDropdown, setShowDoctorDropdown] = useState(false)
    const [showServiceDropdown, setShowServiceDropdown] = useState(false)
    const [highlightedCompanyIndex, setHighlightedCompanyIndex] = useState(-1)
    const [highlightedDoctorIndex, setHighlightedDoctorIndex] = useState(-1)
    const [highlightedServiceIndex, setHighlightedServiceIndex] = useState(-1)



    // Form setup
    const isLabOperator =
        profile?.ruolo === 'amministratore' || profile?.ruolo === 'tecnico_lab'
    const hasSelectedLabCompany = !isLabOperator || !!selectedCompanyId
    const canViewPrices = hasPermission('view_prices')
    const prescriptionSchema = createPrescriptionSchema(isLabOperator)

    const form = useForm<PrescriptionFormInput, unknown, PrescriptionFormData>({
        resolver: zodResolver(prescriptionSchema),
        defaultValues: {
            NomePaziente: '',
            medico_id: '',
            prestazione_id: '',
            company_id: '',
            selectedTeeth: [],
            selectedImplants: [],
            tipo_prova: '',
            prova_id: 0,
            tinta: '',
            color_id: 0,
            dataapp: '',
            timeapp: '',
            scansione_intraorale: false,
            chiama_medico: false,
            presa_fisica_corriere: false,
            note: '',
        }
    })

    // Operatori laboratorio: possono scegliere l'azienda su cui operare.
    const companies = useQuery(
        api.companies.listCompanies,
        isLabOperator ? { paginationOpts: { numItems: 100, cursor: null } } : "skip"
    )
    const companiesList = companies?.page || []
    const filteredCompaniesList = companiesList.filter((company: any) =>
        companySearchTerm.trim() === '' || company.nome.toLowerCase().includes(companySearchTerm.toLowerCase())
    )

    // Get doctors based on user permissions and selected company (for admins)
    const queriedDoctors = useQuery(
        api.profiles.listProfiles,
        profile && profile.ruolo !== 'dentista' && hasSelectedLabCompany ?
            {
                paginationOpts: { numItems: 100, cursor: null },
                viewerKey: profile._id,
                // Clinic owners can see themselves + dentists from their company
                // Dentists can only see themselves
                // Operatori laboratorio: medici della società selezionata o tutti.
                ...(profile.ruolo === 'clinic_owner'
                        ? {
                            ruolo: ['dentista', 'clinic_owner'], // Include both dentists and clinic_owners
                            company_id: profile.company_id
                        }
                        : isLabOperator
                            ? {
                                ruolo: ['dentista', 'clinic_owner'],
                                company_id: selectedCompanyId as Id<'companies'>
                            }
                            : { ruolo: ['dentista', 'clinic_owner'] } // Fallback
                )
            } : "skip"
    )
    const doctors = profile?.ruolo === 'dentista' ? { page: [profile] } : queriedDoctors
    const doctorsList = doctors?.page || []
    const filteredDoctorsList = doctorsList.filter((doctor: any) => doctor.ruolo === 'clinic_owner' || doctor.ruolo === 'dentista')
    const filteredDoctorsBySearch = filteredDoctorsList.filter((doctor: any) =>
        doctorSearchTerm.trim() === '' ||
        `${doctor.nome} ${doctor.cognome}`.toLowerCase().includes(doctorSearchTerm.toLowerCase()) ||
        doctor.email?.toLowerCase().includes(doctorSearchTerm.toLowerCase())
    )

    // Il backend unisce il listino societario a quello personale del medico scelto.
    const services = useQuery(
        api.listino.getActiveServices,
        prescriptionServiceQueryArgs(profile, selectedCompanyId, selectedDoctorId)
    )

    // Get selected service details (including laboratory_tests)
    const selectedService = useQuery(
        api.listino.getListinoItem,
        form.watch('prestazione_id') ? { listino_id: form.watch('prestazione_id') as Id<'listino'> } : "skip"
    )

    const effectiveCompanyIdForPhases: Id<'companies'> | undefined = (() => {
        if (isLabOperator) {
            const fromForm = form.watch('company_id')
            if (fromForm) return fromForm as Id<'companies'>
            if (selectedCompanyId) return selectedCompanyId as Id<'companies'>
            return undefined
        }
        return profile?.company_id as Id<'companies'> | undefined
    })()

    const prestazioneIdWatch = form.watch('prestazione_id')
    const effectivePhases = useQuery(
        api.companyPhases.getEffectivePhasesForPrescription,
        effectiveCompanyIdForPhases && prestazioneIdWatch && prescriptionMode === 'single'
            ? {
                  company_id: effectiveCompanyIdForPhases,
                  listino_id: prestazioneIdWatch as Id<'listino'>,
              }
            : 'skip'
    )

    const filteredServicesBySearch = (services || []).filter((service: any) => {
        const label = (service.nome_visualizzato || service.nome_prestazione || '').toLowerCase()
        return serviceSearchTerm.trim() === '' || label.includes(serviceSearchTerm.toLowerCase())
    })

    // Colors from PrimoUp API
    const [colors, setColors] = useState<any[]>([])
    const [colorsLoading, setColorsLoading] = useState(false)
    const getColorsAction = useAction(api.primoup.getColors)

    // Mutations
    const createPrescription = useMutation(api.prescriptions.createPrescription)
    const createMixedPrescriptions = useMutation(api.prescriptions.createMixedPrescriptions)
    const generateUploadUrl = useMutation(api.files.generateUploadUrl)
    const saveFileMetadata = useMutation(api.files.saveFileMetadata)
    const syncAttachmentToPrimoUp = useAction(api.primoup.syncPrescriptionAttachmentToPrimoUp)
    const calculatePrice = useQuery(
        api.listino.calculatePrice,
        canViewPrices &&
        selectedTeeth.length > 0 &&
        form.watch('prestazione_id')
            ? {
                serviceId: form.watch('prestazione_id') as Id<'listino'>,
                selectedTeeth,
                selectedImplants,
            }
            : "skip"
    )
    // Load colors from PrimoUp API
    useEffect(() => {
        const loadColors = async () => {
            setColorsLoading(true)
            try {
                const colorsData = await getColorsAction({})
                setColors(colorsData)
            } catch (error) {
                console.error('Error loading colors:', error)
                setColors([])
            } finally {
                setColorsLoading(false)
            }
        }

        loadColors()
    }, [getColorsAction])

    // Update form when teeth selection changes
    useEffect(() => {
        form.setValue('selectedTeeth', selectedTeeth, { shouldValidate: hasAttemptedSubmit })
        form.setValue('selectedImplants', selectedImplants, { shouldValidate: hasAttemptedSubmit })
        if (selectedTeeth.length > 0) form.clearErrors('selectedTeeth')
    }, [selectedTeeth, selectedImplants, form, hasAttemptedSubmit])

    // Update price calculation
    useEffect(() => {
        if (!canViewPrices) {
            setCalculatedPrice(0)
        } else if (calculatePrice) {
            setCalculatedPrice(calculatePrice.finalPrice)
        }
    }, [calculatePrice, canViewPrices])

    // In creazione si parte dalla prima fase effettiva. L'utente può comunque
    // scegliere una fase successiva: le regole di salto restano applicate.
    useEffect(() => {
        if (prescriptionMode !== 'single' || !effectivePhases) return
        const phases = effectivePhases.phases || []
        const currentName = form.getValues('tipo_prova')
        const currentPhase = phases.find((phase) => phase.name === currentName)
        if (currentPhase) {
            form.setValue('prova_id', Number(currentPhase.id) || 0)
            return
        }
        const firstPhase = phases[0]
        form.setValue('tipo_prova', firstPhase?.name || '')
        form.setValue('prova_id', Number(firstPhase?.id) || 0)
    }, [effectivePhases, form, prescriptionMode])

    // Mixed: intersezione delle fasi effettive (config società, catalogo come fallback).
    useEffect(() => {
        if (prescriptionMode !== 'mixed') return
        const ids = mixedRows
            .map((r) => r.prestazione_id)
            .filter((id): id is string => !!id)
        if (ids.length === 0 || !effectiveCompanyIdForPhases) {
            setSharedTests([])
            return
        }
        let cancelled = false
        setSharedTestsLoading(true)
        ;(async () => {
            try {
                const items = await Promise.all(
                    ids.map((id) =>
                        convex.query(api.companyPhases.getEffectivePhasesForPrescription, {
                            company_id: effectiveCompanyIdForPhases,
                            listino_id: id as Id<'listino'>,
                        })
                    )
                )
                if (cancelled) return
                const lists: Array<Array<{ name: string; display_name?: string }>> = items.map(
                    (item) => item.phases
                )
                // Intersezione per name (case-insensitive)
                const [first, ...rest] = lists
                const normalize = (s: string) => s.trim().toLowerCase()
                const intersection: Array<{ name: string; display_name?: string }> = []
                if (first) {
                    for (const t of first) {
                        const name = t?.name
                        if (!name) continue
                        const key = normalize(name)
                        const inAll = rest.every((arr) =>
                            arr.some((x) => x?.name && normalize(x.name) === key)
                        )
                        if (inAll && !intersection.some((i) => normalize(i.name) === key)) {
                            const matchingTests = [
                                t,
                                ...rest.map((tests) =>
                                    tests.find((candidate) => normalize(candidate.name) === key)
                                ),
                            ].filter((test): test is NonNullable<typeof test> => Boolean(test))
                            const labels = matchingTests.map(laboratoryTestLabel)
                            const sharedLabel = labels.every((label) => label === labels[0])
                                ? labels[0]
                                : name
                            intersection.push({
                                name,
                                ...(sharedLabel !== name ? { display_name: sharedLabel } : {}),
                            })
                        }
                    }
                }
                setSharedTests(intersection)
                // Mantieni una scelta valida; altrimenti parti dalla prima fase comune.
                const current = form.getValues('tipo_prova')
                const currentIsAvailable = intersection.some(
                    (test) => normalize(test.name) === normalize(current || '')
                )
                if (!currentIsAvailable) {
                    form.setValue('tipo_prova', intersection[0]?.name || '')
                    form.setValue('prova_id', intersection.length > 0 ? 1 : 0)
                }
            } catch (err) {
                console.error('Errore caricamento fasi effettive per modalità mista:', err)
                if (!cancelled) setSharedTests([])
            } finally {
                if (!cancelled) setSharedTestsLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        prescriptionMode,
        effectiveCompanyIdForPhases,
        mixedRows.map((r) => r.prestazione_id).join('|'),
    ])

    const minAppointmentDateTime = new Date(Date.now() + 60 * 60 * 1000)
    const minDateString = `${minAppointmentDateTime.getFullYear()}-${String(minAppointmentDateTime.getMonth() + 1).padStart(2, '0')}-${String(minAppointmentDateTime.getDate()).padStart(2, '0')}`
    const minTimeString = `${String(minAppointmentDateTime.getHours()).padStart(2, '0')}:${String(minAppointmentDateTime.getMinutes()).padStart(2, '0')}`

    const uploadSelectedAttachments = async (
        prescriptionId: Id<'prescriptions'>
    ): Promise<string[]> => {
        const failures: string[] = []
        setUploadingFiles(true)
        try {
            for (const file of selectedFiles) {
                const validationError = getPrimoUpAttachmentValidationError({
                    filename: file.name,
                    contentType: file.type,
                })
                if (validationError) {
                    failures.push(`${file.name}: ${validationError}`)
                    continue
                }
                try {
                    const uploadUrl = await generateUploadUrl({})
                    const uploadResult = await fetch(uploadUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': file.type },
                        body: file,
                    })
                    if (!uploadResult.ok) {
                        throw new Error('caricamento locale non riuscito')
                    }
                    const { storageId } = await uploadResult.json()
                    const attachmentId = await saveFileMetadata({
                        prescriptions_id: prescriptionId,
                        file_id: storageId,
                        filename: file.name,
                        content_type: file.type,
                        file_size: file.size,
                    })
                    const syncResult = await syncAttachmentToPrimoUp({ attachmentId })
                    if (
                        syncResult.status === 'failed' ||
                        syncResult.status === 'uncertain' ||
                        syncResult.status === 'unsupported' ||
                        syncResult.status === 'missing'
                    ) {
                        failures.push(
                            `${file.name}: ${syncResult.message ?? 'invio a PrimoUp non riuscito'}`
                        )
                    }
                } catch (error) {
                    failures.push(
                        `${file.name}: ${error instanceof Error ? error.message : 'caricamento non riuscito'}`
                    )
                }
            }
        } finally {
            setUploadingFiles(false)
        }
        return failures
    }


    const onSubmit = async (data: PrescriptionFormData, event?: React.BaseSyntheticEvent) => {
        // Only allow intentional form submission
        if (!isIntentionalSubmit) {
            console.log('Preventing accidental form submission')
            if (event) {
                event.preventDefault()
            }
            return
        }
        
        // Reset the flag after use
        setIsIntentionalSubmit(false)
        let attachmentFailures: string[] = []
        try {
            const serviceForValidation = (services || []).find(
                (service) => service._id === data.prestazione_id
            ) ?? selectedService
            const selectionStatus = getClinicalSelectionStatus({
                selectedTeeth,
                selectedImplants,
                implantTypes,
                pricingMode: serviceForValidation
                    ? resolveServicePricingMode(serviceForValidation)
                    : undefined,
            })
            if (!selectionStatus.valid) {
                setCurrentStep(2)
                setHasAttemptedSubmit(true)
                focusElement('single-clinical-selection-status')
                return
            }
            // Combine date and time properly
            let appointmentDate: number | undefined = undefined;
            if (data.dataapp) {
                const dateStr = data.dataapp;
                const timeStr = data.timeapp || '09:00'; // Default to 9:00 AM if no time specified
                const combinedDateTime = new Date(`${dateStr}T${timeStr}:00`);

                const minAllowedDateTime = new Date(Date.now() + 60 * 60 * 1000)
                if (combinedDateTime.getTime() < minAllowedDateTime.getTime()) {
                    const minReadable = `${minAllowedDateTime.toLocaleDateString('it-IT')} ${minAllowedDateTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                    form.setError('timeapp', {
                        type: 'manual',
                        message: `L'appuntamento deve essere almeno un'ora dopo adesso (${minReadable})`
                    })
                    showFlowError(
                        3,
                        'Appuntamento troppo vicino',
                        `Scegli una data e un orario successivi al ${minReadable}: serve almeno un\u2019ora di anticipo.`,
                        'delivery-time'
                    )
                    return
                }

                appointmentDate = combinedDateTime.getTime();
            }

            const result = await createPrescription({
                NomePaziente: data.NomePaziente,
                medico_id: data.medico_id as Id<'profiles'>,
                prestazione_id: data.prestazione_id as Id<'listino'>,
                company_id: data.company_id ? data.company_id as Id<'companies'> : undefined,
                selectedTeeth,
                selectedImplants,
                implantTypes,
                tipo_prova: data.tipo_prova,
                prova_id : data.prova_id,
                tinta: data.tinta,
                color_id: data.color_id,
                dataapp: appointmentDate,
                timeapp: data.timeapp,
                scansione_intraorale: data.scansione_intraorale,
                chiama_medico: data.chiama_medico,
                presa_fisica_corriere: data.presa_fisica_corriere,
                note: data.note,
                prezzo: calculatedPrice,
            })

            // Upload files if any
            if (selectedFiles.length > 0) {
                attachmentFailures = await uploadSelectedAttachments(result.prescriptionId)
            }

            const approvalMessage = result.requiresApproval
                ? 'Prescrizione salvata e inviata al laboratorio per approvare il salto di fase. PrimoUp non è ancora stato chiamato.'
                : ''
            const attachmentMessage = attachmentFailures.length > 0
                ? `La prescrizione è stata creata, ma questi allegati richiedono attenzione:\n${attachmentFailures.join('\n')}`
                : ''
            const confirmationMessage = [approvalMessage, attachmentMessage].filter(Boolean).join('\n\n')
            if (confirmationMessage) {
                alert(confirmationMessage)
            }
            onSuccess?.(result.prescriptionId)
        } catch (error) {
            console.error('Error creating prescription:', error)
            showFlowError(
                4,
                'Prescrizione non creata',
                getReadablePrescriptionError(
                    error,
                    'Non è stato possibile completare la creazione. Controlla i dati e riprova; se il problema continua, contatta PrimoLab.'
                )
            )
        }
    }

    const handleSelectionChange = (
        teeth: string[],
        implants: string[],
        types: Record<string, string>
    ) => {
        const selectedService = form.watch('prestazione_id')
        if ((teeth.length > 0 || implants.length > 0) && !selectedService) return
        
        setSelectedTeeth(teeth)
        setSelectedImplants(implants)
        setImplantTypes(types)
        setFlowError((current) => current?.step === 2 ? null : current)
    }

    // Handle company selection (for admins)
    const handleCompanyChange = (companyId: string) => {
        setSelectedCompanyId(companyId)
        form.setValue('company_id', companyId)
        // Clear doctor and prestazione selection when company changes
        form.setValue('medico_id', '')
        form.setValue('prestazione_id', '')
        form.setValue('tipo_prova', '')
        form.setValue('prova_id', 0)
        setSelectedDoctorId('')
        setDoctorSearchTerm('')
        setServiceSearchTerm('')
        setSelectedTeeth([])
        setSelectedImplants([])
        setImplantTypes({})
        setMixedRows([emptyMixedRow(), emptyMixedRow()])
    }

    // Handle doctor selection for anyone creating a prescription on their behalf.
    const handleDoctorChange = (doctorId: string) => {
        form.setValue('medico_id', doctorId)
        setSelectedDoctorId(doctorId)

        // Clear prestazione selection when doctor changes
        form.setValue('prestazione_id', '')
        form.setValue('tipo_prova', '')
        form.setValue('prova_id', 0)
        setServiceSearchTerm('')
        setMixedRows([emptyMixedRow(), emptyMixedRow()])

        // La società scelta resta il contesto dell'ordine: un medico può essere
        // collegato a più società e la sua società principale non deve sovrascriverla.
    }

    const handleServiceChange = (serviceId: string, serviceLabel?: string) => {
        form.setValue('prestazione_id', serviceId)
        form.setValue('tipo_prova', '')
        form.setValue('prova_id', 0)
        if (serviceLabel !== undefined) {
            setServiceSearchTerm(serviceLabel)
        }
    }

    useEffect(() => {
        if (!showCompanyDropdown || filteredCompaniesList.length === 0) {
            setHighlightedCompanyIndex(-1)
        } else if (highlightedCompanyIndex >= filteredCompaniesList.length) {
            setHighlightedCompanyIndex(0)
        }
    }, [showCompanyDropdown, filteredCompaniesList.length, highlightedCompanyIndex])

    useEffect(() => {
        if (!showDoctorDropdown || filteredDoctorsBySearch.length === 0) {
            setHighlightedDoctorIndex(-1)
        } else if (highlightedDoctorIndex >= filteredDoctorsBySearch.length) {
            setHighlightedDoctorIndex(0)
        }
    }, [showDoctorDropdown, filteredDoctorsBySearch.length, highlightedDoctorIndex])

    useEffect(() => {
        if (!showServiceDropdown || filteredServicesBySearch.length === 0) {
            setHighlightedServiceIndex(-1)
        } else if (highlightedServiceIndex >= filteredServicesBySearch.length) {
            setHighlightedServiceIndex(0)
        }
    }, [showServiceDropdown, filteredServicesBySearch.length, highlightedServiceIndex])

    useEffect(() => {
        if (!showCompanyDropdown || highlightedCompanyIndex < 0) return
        const element = document.getElementById(`company-option-${highlightedCompanyIndex}`)
        if (element) {
            element.scrollIntoView({ block: 'nearest' })
        }
    }, [showCompanyDropdown, highlightedCompanyIndex])

    useEffect(() => {
        if (!showDoctorDropdown || highlightedDoctorIndex < 0) return
        const element = document.getElementById(`doctor-option-${highlightedDoctorIndex}`)
        if (element) {
            element.scrollIntoView({ block: 'nearest' })
        }
    }, [showDoctorDropdown, highlightedDoctorIndex])

    useEffect(() => {
        if (!showServiceDropdown || highlightedServiceIndex < 0) return
        const element = document.getElementById(`service-option-${highlightedServiceIndex}`)
        if (element) {
            element.scrollIntoView({ block: 'nearest' })
        }
    }, [showServiceDropdown, highlightedServiceIndex])

    useEffect(() => {
        if (previousStepRef.current === currentStep) return
        previousStepRef.current = currentStep
        stepHeadingRef.current?.focus()
    }, [currentStep])

    if (!profile) {
        return <div>Errore: Profilo utente non trovato</div>
    }

    const handleMixedSubmit = async () => {
        setHasAttemptedSubmit(true)
        const data = form.getValues()

        if (!data.NomePaziente?.trim()) {
            showFlowError(
                1,
                'Manca il nome del paziente',
                'Inserisci nome e cognome del paziente per creare la prescrizione mista.'
            )
            return
        }
        if (!data.medico_id) {
            showFlowError(
                1,
                'Manca il medico',
                'Cerca e seleziona il medico associato alla prescrizione per continuare.'
            )
            return
        }
        if (isLabOperator && !data.company_id) {
            showFlowError(
                1,
                'Manca la clinica',
                'Seleziona la clinica per caricare i medici e le prestazioni disponibili.'
            )
            return
        }
        if (data.scansione_intraorale === data.presa_fisica_corriere) {
            showFlowError(
                3,
                'Modalità impronta non valida',
                data.scansione_intraorale
                    ? 'Hai selezionato sia digitale sia analogica. Mantieni una sola modalità per continuare.'
                    : 'Manca la modalità impronta. Seleziona digitale oppure analogica per continuare.'
            )
            return
        }

        // Validazioni per riga (solo campi per-riga: prestazione, odontogramma, nota libera)
        for (let i = 0; i < mixedRows.length; i++) {
            const row = mixedRows[i]
            if (!row.prestazione_id) {
                showFlowError(
                    2,
                    `Componente ${i + 1} incompleto`,
                    `Manca la prestazione del componente ${i + 1}. Selezionala per abilitare denti e impianti.`,
                    `mixed-service-${i}`
                )
                return
            }
            const service = (services || []).find((item) => item._id === row.prestazione_id)
            const selectionStatus = getClinicalSelectionStatus({
                selectedTeeth: row.selectedTeeth,
                selectedImplants: row.selectedImplants,
                implantTypes: row.implantTypes,
                pricingMode: service ? resolveServicePricingMode(service) : undefined,
            })
            if (!selectionStatus.valid) {
                showFlowError(
                    2,
                    `Componente ${i + 1}: ${selectionStatus.title}`,
                    selectionStatus.description,
                    `mixed-component-${i}-clinical-selection-status`
                )
                return
            }
        }

        // Validazioni dei campi shared (uniche per tutto il gruppo)
        const sharedTipoProva = (data.tipo_prova || '').trim()
        if (!sharedTipoProva) {
            showFlowError(
                3,
                'Manca la fase di lavoro',
                'Seleziona la fase da applicare a tutti i componenti della prescrizione.'
            )
            return
        }
        // color_id 0 = tinta scelta ma risoluzione colore fallita
        if (!data.tinta?.trim() || !data.color_id) {
            showFlowError(
                3,
                'Manca la tinta',
                'Seleziona la tinta dall\u2019elenco: verrà applicata a tutti i componenti.'
            )
            return
        }

        let appointmentDate: number | undefined = undefined
        if (data.dataapp) {
            const dateStr = data.dataapp
            const timeStr = data.timeapp || '09:00'
            const combinedDateTime = new Date(`${dateStr}T${timeStr}:00`)
            const minAllowedDateTime = new Date(Date.now() + 60 * 60 * 1000)
            if (combinedDateTime.getTime() < minAllowedDateTime.getTime()) {
                const minReadable = `${minAllowedDateTime.toLocaleDateString('it-IT')} ${minAllowedDateTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                showFlowError(
                    3,
                    'Appuntamento troppo vicino',
                    `Scegli una data e un orario successivi al ${minReadable}: serve almeno un\u2019ora di anticipo.`
                )
                return
            }
            appointmentDate = combinedDateTime.getTime()
        } else {
            showFlowError(
                3,
                'Mancano data e ora',
                'Seleziona quando è previsto l\u2019appuntamento per continuare.'
            )
            return
        }

        let attachmentFailures: string[] = []
        setMixedSubmitting(true)
        try {
            type MixedComponentPayload = {
                prestazione_id: Id<'listino'>
                selectedTeeth: string[]
                selectedImplants: string[]
                implantTypes: Record<string, string>
                tipo_prova: string
                prova_id: number
                tinta: string
                color_id: number
                dataapp: number
                timeapp: string
                scansione_intraorale: boolean
                chiama_medico: boolean
                presa_fisica_corriere: boolean
                note: string
                prezzo: number
            }
            const components: MixedComponentPayload[] = []
            const normalize = (s: string) => s.trim().toLowerCase()
            for (let i = 0; i < mixedRows.length; i++) {
                const row = mixedRows[i]
                if (!effectiveCompanyIdForPhases) {
                    throw new Error('Clinica non disponibile per risolvere le fasi')
                }
                const [priceResult, effective] = await Promise.all([
                    canViewPrices
                        ? convex.query(api.listino.calculatePrice, {
                              serviceId: row.prestazione_id as Id<'listino'>,
                              selectedTeeth: row.selectedTeeth,
                          })
                        : Promise.resolve(null),
                    convex.query(api.companyPhases.getEffectivePhasesForPrescription, {
                        company_id: effectiveCompanyIdForPhases,
                        listino_id: row.prestazione_id as Id<'listino'>,
                    }),
                ])
                if (canViewPrices && !priceResult) {
                    throw new Error('Impossibile calcolare il prezzo per un componente')
                }
                const matchedIndex = effective.phases.findIndex(
                    (t) => t?.name && normalize(t.name) === normalize(sharedTipoProva)
                )
                const matched = effective.phases[matchedIndex]
                if (!matched) {
                    throw new Error(
                        `La fase di lavoro "${sharedTipoProva}" non è disponibile per il componente ${i + 1}.`
                    )
                }
                const provaId = Number(matched.id) || 0

                components.push({
                    prestazione_id: row.prestazione_id as Id<'listino'>,
                    selectedTeeth: row.selectedTeeth,
                    selectedImplants: row.selectedImplants,
                    implantTypes: row.implantTypes || {},
                    tipo_prova: sharedTipoProva,
                    prova_id: provaId,
                    tinta: data.tinta,
                    color_id: data.color_id,
                    dataapp: appointmentDate as number,
                    timeapp: data.timeapp || '09:00',
                    scansione_intraorale: !!data.scansione_intraorale,
                    chiama_medico: !!data.chiama_medico,
                    presa_fisica_corriere: !!data.presa_fisica_corriere,
                    note: row.note,
                    // Il backend ricalcola sempre il prezzo dal listino. I ruoli
                    // senza view_prices non ricevono alcun valore economico.
                    prezzo: priceResult?.finalPrice ?? 0,
                })
            }

            const result = await createMixedPrescriptions({
                NomePaziente: data.NomePaziente.trim(),
                medico_id: data.medico_id as Id<'profiles'>,
                company_id: data.company_id ? (data.company_id as Id<'companies'>) : undefined,
                note_gruppo: data.note,
                shared_note: data.note,
                components,
            })

            if (selectedFiles.length > 0 && result.prescriptionIds[0]) {
                attachmentFailures = await uploadSelectedAttachments(result.prescriptionIds[0])
            }

            const successMessage = result.requiresApproval
                ? `${result.numbers.length} richieste salvate in attesa di approvazione del salto fase. PrimoUp non è ancora stato chiamato.`
                : `${result.numbers.length} richieste salvate; sincronizzazione con il laboratorio avviata. Numeri: ${result.numbers.join(', ')}`
            const attachmentMessage = attachmentFailures.length > 0
                ? `Questi allegati richiedono attenzione:\n${attachmentFailures.join('\n')}`
                : ''
            alert([successMessage, attachmentMessage].filter(Boolean).join('\n\n'))
            onSuccess?.(result.prescriptionIds[0])
        } catch (error) {
            console.error('Error creating mixed prescription:', error)
            showFlowError(
                4,
                'Prescrizione mista non creata',
                getReadablePrescriptionError(
                    error,
                    'Non è stato possibile creare e sincronizzare tutti i componenti. Nessun gruppo parziale è stato inviato: controlla i dati e riprova.'
                )
            )
        } finally {
            setMixedSubmitting(false)
        }
    }

    const watchedValues = form.watch()
    const selectedCompany = companiesList.find((company: any) => company._id === watchedValues.company_id)
    const doctorForSummary = doctorsList.find((doctor: any) => doctor._id === watchedValues.medico_id)
    const serviceForSummary = (services || []).find((service: any) => service._id === watchedValues.prestazione_id)
    const selectedServiceLabel = serviceForSummary?.nome_visualizzato || serviceForSummary?.nome_prestazione || ''
    const selectedElementParts = [
        selectedTeeth.length > 0 ? `Denti: ${selectedTeeth.join(', ')}` : '',
        selectedImplants.length > 0 ? `Impianti: ${selectedImplants.join(', ')}` : '',
    ].filter(Boolean)
    const selectedElementLabel = selectedElementParts.length > 0 ? selectedElementParts.join(' · ') : 'Non selezionati'
    const impressionMode = watchedValues.scansione_intraorale
        ? 'Digitale'
        : watchedValues.presa_fisica_corriere
            ? 'Analogica'
            : 'Non selezionata'
    const appointmentSummary = watchedValues.dataapp && watchedValues.timeapp
        ? new Intl.DateTimeFormat('it-IT', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(`${watchedValues.dataapp}T${watchedValues.timeapp}`))
        : 'Non impostato'

    const serviceForClinicalValidation = serviceForSummary ?? selectedService
    const singlePricingMode = serviceForClinicalValidation
        ? resolveServicePricingMode(serviceForClinicalValidation)
        : undefined
    const singleSelectionStatus = getClinicalSelectionStatus({
        selectedTeeth,
        selectedImplants,
        implantTypes,
        pricingMode: singlePricingMode,
    })
    const mixedSelectionStatuses = mixedRows.map((row) => {
        const service = (services || []).find((item) => item._id === row.prestazione_id)
        return getClinicalSelectionStatus({
            selectedTeeth: row.selectedTeeth,
            selectedImplants: row.selectedImplants,
            implantTypes: row.implantTypes,
            pricingMode: service ? resolveServicePricingMode(service) : undefined,
        })
    })

    const patientStepComplete = Boolean(
        watchedValues.NomePaziente?.trim() &&
        watchedValues.medico_id &&
        (!isLabOperator || watchedValues.company_id)
    )
    const configurationStepComplete = prescriptionMode === 'single'
        ? Boolean(watchedValues.prestazione_id && singleSelectionStatus.valid)
        : mixedRows.length > 0 && mixedRows.every(
            (row, index) => Boolean(row.prestazione_id && mixedSelectionStatuses[index]?.valid)
        )
    const detailsStepComplete = Boolean(
        watchedValues.tipo_prova &&
        watchedValues.tinta &&
        watchedValues.dataapp &&
        watchedValues.timeapp &&
        (watchedValues.scansione_intraorale || watchedValues.presa_fisica_corriere)
    )
    const workflowSteps = [
        { number: 1, label: 'Paziente', complete: patientStepComplete },
        { number: 2, label: 'Configurazione clinica', complete: configurationStepComplete },
        { number: 3, label: 'Dettagli laboratorio', complete: detailsStepComplete },
        { number: 4, label: 'Verifica e invio', complete: false },
    ]

    const goToNextStep = async () => {
        let valid = true
        let invalidTargetId: string | null = null

        if (currentStep === 1) {
            const fields: Array<keyof PrescriptionFormInput> = ['NomePaziente', 'medico_id']
            if (isLabOperator) fields.push('company_id')
            valid = await form.trigger(fields)
        } else if (currentStep === 2) {
            if (prescriptionMode === 'single') {
                const serviceValid = await form.trigger(['prestazione_id'])
                valid = serviceValid && singleSelectionStatus.valid
                invalidTargetId = !serviceValid
                    ? 'service-search'
                    : !singleSelectionStatus.valid
                        ? 'single-clinical-selection-status'
                        : null
            } else {
                const invalidRowIndex = mixedRows.findIndex(
                    (row, index) => !row.prestazione_id || !mixedSelectionStatuses[index]?.valid
                )
                valid = mixedRows.length > 0 && invalidRowIndex === -1
                if (invalidRowIndex >= 0) {
                    invalidTargetId = mixedRows[invalidRowIndex].prestazione_id
                        ? `mixed-component-${invalidRowIndex}-clinical-selection-status`
                        : `mixed-service-${invalidRowIndex}`
                }
            }
        } else if (currentStep === 3) {
            valid = await form.trigger([
                'tipo_prova',
                'tinta',
                'dataapp',
                'timeapp',
                'scansione_intraorale',
            ])
        }

        if (!valid) {
            setHasAttemptedSubmit(true)
            if (invalidTargetId) focusElement(invalidTargetId)
            return
        }

        setHasAttemptedSubmit(false)
        setFlowError(null)
        form.clearErrors()
        setCurrentStep((step) => Math.min(step + 1, 4))
    }

    const handleInvalidSubmit = (errors: FieldErrors<PrescriptionFormInput>) => {
        setHasAttemptedSubmit(true)

        if (errors.NomePaziente || errors.medico_id || errors.company_id) {
            setCurrentStep(1)
            focusElement(
                errors.NomePaziente
                    ? 'patient-name'
                    : errors.company_id
                        ? 'company-search'
                        : 'doctor-search'
            )
            return
        }

        if (errors.prestazione_id || errors.selectedTeeth) {
            setCurrentStep(2)
            focusElement(
                errors.prestazione_id
                    ? 'service-search'
                    : 'single-clinical-selection-status'
            )
            return
        }

        showFlowError(
            3,
            'Completa i dettagli per il laboratorio',
            'Uno o più campi obbligatori sono incompleti. Correggi i campi evidenziati per arrivare alla verifica finale.'
        )
    }

    return (
        <LoadingOverlay
            isLoading={form.formState.isSubmitting || mixedSubmitting}
            text={mixedSubmitting ? 'Sincronizzazione con laboratorio…' : 'Creazione prescrizione...'}
        >
            <form 
                onSubmit={form.handleSubmit(onSubmit, handleInvalidSubmit)}
                className="space-y-6"
            >
                <nav aria-label="Avanzamento prescrizione" className="overflow-x-auto pb-1">
                    <ol className="grid min-w-0 grid-cols-4 gap-1 sm:min-w-[720px] sm:gap-0">
                        {workflowSteps.map((step, index) => {
                            const isActive = currentStep === step.number
                            const isPast = currentStep > step.number
                            return (
                                <li key={step.number} className="relative flex min-w-0 flex-col items-center sm:flex-row">
                                    <button
                                        type="button"
                                        onClick={() => step.number <= currentStep && setCurrentStep(step.number)}
                                        disabled={step.number > currentStep}
                                        aria-current={isActive ? 'step' : undefined}
                                        aria-label={`Fase ${step.number} di ${workflowSteps.length}: ${step.label}${isActive ? ', fase corrente' : step.complete || isPast ? ', completata' : ''}`}
                                        className="group relative z-10 flex w-full min-w-0 flex-col items-center gap-1 bg-transparent text-center disabled:cursor-default sm:w-auto sm:flex-row sm:gap-3 sm:bg-white sm:pr-4 sm:text-left"
                                    >
                                        <span
                                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                                                isActive
                                                    ? 'border-primary bg-primary text-white shadow-[0_6px_16px_rgba(255,107,53,0.24)]'
                                                    : isPast || step.complete
                                                        ? 'border-secondary bg-secondary text-white'
                                                        : 'border-gray-300 bg-white text-gray-500'
                                            }`}
                                        >
                                            {isPast || (!isActive && step.complete) ? <Check className="h-4 w-4" aria-hidden="true" /> : step.number}
                                        </span>
                                        <span className={`min-h-8 text-[11px] font-medium leading-4 sm:min-h-0 sm:truncate sm:text-sm ${isActive ? 'text-gray-950' : 'text-gray-500'}`}>
                                            {step.label}
                                        </span>
                                    </button>
                                    {index < workflowSteps.length - 1 && (
                                        <span
                                            aria-hidden="true"
                                            className={`absolute left-[calc(50%+20px)] right-[calc(-50%+20px)] top-5 h-px sm:static sm:min-w-6 sm:flex-1 ${isPast ? 'bg-primary' : 'bg-gray-200'}`}
                                        />
                                    )}
                                </li>
                            )
                        })}
                    </ol>
                </nav>

                <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                    <div className="min-w-0 rounded-xl border border-gray-200 bg-white shadow-[0_10px_32px_rgba(15,23,42,0.05)]">
                        <div className="border-b border-gray-100 px-5 py-5 sm:px-7">
                            <h2
                                ref={stepHeadingRef}
                                tabIndex={-1}
                                className="text-xl font-semibold tracking-[-0.02em] text-gray-950 focus:outline-hidden"
                            >
                                {currentStep === 2 ? 'Scegli la prestazione e gli elementi dentali' : workflowSteps[currentStep - 1].label}
                            </h2>
                            <p className="mt-1 text-sm text-gray-600">
                                {currentStep === 1 && 'Identifica il paziente e il medico associato alla richiesta.'}
                                {currentStep === 2 && 'Seleziona prima la prestazione, poi completa la selezione dentale richiesta. Gli impianti sono facoltativi.'}
                                {currentStep === 3 && 'Completa le indicazioni operative già previste per il laboratorio.'}
                                {currentStep === 4 && 'Controlla i dati inseriti prima di creare la prescrizione.'}
                            </p>
                        </div>
                        <div className="p-5 sm:p-7">
                {flowError?.step === currentStep && (
                    <div
                        id="prescription-flow-error"
                        tabIndex={-1}
                        role="alert"
                        className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-950 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold">{flowError.title}</p>
                            <p className="mt-0.5 text-sm leading-5">{flowError.description}</p>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setFlowError(null)}
                            aria-label="Chiudi il messaggio"
                            className="-mr-2 -mt-1 h-8 w-8 p-0 text-red-800 hover:bg-red-100 hover:text-red-950"
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </Button>
                    </div>
                )}
                {currentStep === 1 && (
                <div className="space-y-6">
                {/* Patient Information */}
                <Card className="border-0 shadow-none">
                    <CardHeader className="px-0 pt-0">
                        <CardTitle className="text-lg">Informazioni paziente</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 px-0 pb-0">
                        <div>
                            <label htmlFor="patient-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Nome paziente *
                            </label>
                            <Input
                                id="patient-name"
                                {...form.register('NomePaziente')}
                                placeholder="Inserisci nome e cognome del paziente"
                                className={form.formState.errors.NomePaziente ? 'form-field-error' : ''}
                            />
                            {form.formState.errors.NomePaziente && (
                                <p className="form-error">{form.formState.errors.NomePaziente.message}</p>
                            )}
                        </div>

                        {/* Selezione società per operatori laboratorio */}
                        {isLabOperator && (
                            <div>
                            <label htmlFor="company-search" className="block text-sm font-medium text-gray-700 mb-1">
                                Clinica *
                            </label>
                                <div className="relative">
                                    <Input
                                        id="company-search"
                                        type="text"
                                        placeholder="Cerca e seleziona clinica"
                                        value={companySearchTerm}
                                        onChange={(e) => {
                                            setCompanySearchTerm(e.target.value)
                                            setShowCompanyDropdown(true)
                                            if (selectedCompanyId) {
                                                handleCompanyChange('')
                                            }
                                        }}
                                        onFocus={() => setShowCompanyDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 120)}
                                        onKeyDown={(e) => {
                                            if (!showCompanyDropdown || filteredCompaniesList.length === 0) return

                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault()
                                                setHighlightedCompanyIndex((prev) => prev < filteredCompaniesList.length - 1 ? prev + 1 : 0)
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault()
                                                setHighlightedCompanyIndex((prev) => prev > 0 ? prev - 1 : filteredCompaniesList.length - 1)
                                            } else if (e.key === 'Enter') {
                                                if (highlightedCompanyIndex >= 0) {
                                                    e.preventDefault()
                                                    const company = filteredCompaniesList[highlightedCompanyIndex]
                                                    handleCompanyChange(company._id)
                                                    setCompanySearchTerm(company.nome)
                                                    setShowCompanyDropdown(false)
                                                }
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault()
                                                setShowCompanyDropdown(false)
                                            }
                                        }}
                                        className={form.formState.errors.company_id ? 'form-field-error' : ''}
                                        role="combobox"
                                        aria-autocomplete="list"
                                        aria-expanded={showCompanyDropdown}
                                        aria-controls="company-options"
                                        aria-activedescendant={highlightedCompanyIndex >= 0 ? `company-option-${highlightedCompanyIndex}` : undefined}
                                    />
                                    {showCompanyDropdown && (
                                        <div id="company-options" role="listbox" className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                            {filteredCompaniesList.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">Nessuna clinica trovata</div>
                                            ) : (
                                                filteredCompaniesList.map((company: any, index: number) => (
                                                    <button
                                                        id={`company-option-${index}`}
                                                        key={company._id}
                                                        type="button"
                                                        role="option"
                                                        aria-selected={highlightedCompanyIndex === index}
                                                        className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 ${highlightedCompanyIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onMouseEnter={() => setHighlightedCompanyIndex(index)}
                                                        onClick={() => {
                                                            handleCompanyChange(company._id)
                                                            setCompanySearchTerm(company.nome)
                                                            setShowCompanyDropdown(false)
                                                        }}
                                                    >
                                                        {company.nome}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                                {form.formState.errors.company_id && (
                                    <p className="form-error">{form.formState.errors.company_id.message}</p>
                                )}
                            </div>
                        )}

                        <div>
                            <label htmlFor="doctor-search" className="block text-sm font-medium text-gray-700 mb-1">
                                Medico *
                            </label>
                            <div className="relative">
                                <Input
                                    id="doctor-search"
                                    type="text"
                                    placeholder={
                                        isLabOperator && !selectedCompanyId
                                            ? 'Seleziona prima una clinica'
                                            : 'Cerca e seleziona medico'
                                    }
                                    disabled={isLabOperator && !selectedCompanyId}
                                    value={doctorSearchTerm}
                                    onChange={(e) => {
                                        setDoctorSearchTerm(e.target.value)
                                        setShowDoctorDropdown(true)
                                        if (form.watch('medico_id')) {
                                            handleDoctorChange('')
                                        }
                                    }}
                                    onFocus={() => setShowDoctorDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowDoctorDropdown(false), 120)}
                                    onKeyDown={(e) => {
                                        if (!showDoctorDropdown || filteredDoctorsBySearch.length === 0) return

                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault()
                                            setHighlightedDoctorIndex((prev) => prev < filteredDoctorsBySearch.length - 1 ? prev + 1 : 0)
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault()
                                            setHighlightedDoctorIndex((prev) => prev > 0 ? prev - 1 : filteredDoctorsBySearch.length - 1)
                                        } else if (e.key === 'Enter') {
                                            if (highlightedDoctorIndex >= 0) {
                                                e.preventDefault()
                                                const doctor = filteredDoctorsBySearch[highlightedDoctorIndex]
                                                handleDoctorChange(doctor._id)
                                                setDoctorSearchTerm(`${doctor.nome} ${doctor.cognome}`)
                                                setShowDoctorDropdown(false)
                                            }
                                        } else if (e.key === 'Escape') {
                                            e.preventDefault()
                                            setShowDoctorDropdown(false)
                                        }
                                    }}
                                    className={form.formState.errors.medico_id ? 'border-red-500' : ''}
                                    role="combobox"
                                    aria-autocomplete="list"
                                    aria-expanded={showDoctorDropdown}
                                    aria-controls="doctor-options"
                                    aria-activedescendant={highlightedDoctorIndex >= 0 ? `doctor-option-${highlightedDoctorIndex}` : undefined}
                                />
                                {showDoctorDropdown && (
                                    <div id="doctor-options" role="listbox" className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                        {filteredDoctorsBySearch.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">Nessun medico trovato</div>
                                        ) : (
                                            filteredDoctorsBySearch.map((doctor: any, index: number) => (
                                                <button
                                                    id={`doctor-option-${index}`}
                                                    key={doctor._id}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={highlightedDoctorIndex === index}
                                                    className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 ${highlightedDoctorIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onMouseEnter={() => setHighlightedDoctorIndex(index)}
                                                    onClick={() => {
                                                        handleDoctorChange(doctor._id)
                                                        setDoctorSearchTerm(`${doctor.nome} ${doctor.cognome}`)
                                                        setShowDoctorDropdown(false)
                                                    }}
                                                >
                                                    {doctor.nome} {doctor.cognome}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {form.formState.errors.medico_id && (
                                <p className="form-error">{form.formState.errors.medico_id.message}</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 border-t border-gray-100 shadow-none">
                    <CardHeader className="px-0 pt-6">
                        <CardTitle className="text-lg">Modalità prescrizione</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 px-0 pb-0">
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant={prescriptionMode === 'single' ? 'default' : 'outline'}
                                onClick={() => setPrescriptionMode('single')}
                            >
                                Singola
                            </Button>
                            <Button
                                type="button"
                                variant={prescriptionMode === 'mixed' ? 'default' : 'outline'}
                                onClick={() => setPrescriptionMode('mixed')}
                            >
                                Mista
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                            La prescrizione mista crea più ordini collegati: se la sincronizzazione con il
                            laboratorio fallisce per uno di essi, l&apos;intero gruppo viene annullato
                            (nessuno stato parziale).
                        </p>
                    </CardContent>
                </Card>
                </div>
                )}

                {currentStep === 2 && (
                <div className="space-y-6">
                {prescriptionMode === 'single' && (
                <>
                <div className="space-y-5">
                {/* Service Selection */}
                <section aria-labelledby="service-configuration-heading" className="border-b border-gray-200 pb-5">
                    <h3 id="service-configuration-heading" className="mb-3 text-base font-semibold text-gray-950">Prestazione</h3>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="service-search" className="block text-sm font-medium text-gray-700 mb-1">
                                Prestazione *
                            </label>
                            <input type="hidden" {...form.register('prestazione_id')} />
                            <div className="relative">
                                <Input
                                    id="service-search"
                                    type="text"
                                    placeholder="Cerca e seleziona prestazione"
                                    value={serviceSearchTerm}
                                    onChange={(e) => {
                                        setServiceSearchTerm(e.target.value)
                                        setShowServiceDropdown(true)
                                        if (form.watch('prestazione_id')) {
                                            handleServiceChange('')
                                        }
                                    }}
                                    onFocus={() => setShowServiceDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowServiceDropdown(false), 120)}
                                    onKeyDown={(e) => {
                                        if (!showServiceDropdown || filteredServicesBySearch.length === 0) return

                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault()
                                            setHighlightedServiceIndex((prev) => prev < filteredServicesBySearch.length - 1 ? prev + 1 : 0)
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault()
                                            setHighlightedServiceIndex((prev) => prev > 0 ? prev - 1 : filteredServicesBySearch.length - 1)
                                        } else if (e.key === 'Enter') {
                                            if (highlightedServiceIndex >= 0) {
                                                e.preventDefault()
                                                const service = filteredServicesBySearch[highlightedServiceIndex]
                                                handleServiceChange(service._id, service.nome_visualizzato || service.nome_prestazione)
                                                setShowServiceDropdown(false)
                                            }
                                        } else if (e.key === 'Escape') {
                                            e.preventDefault()
                                            setShowServiceDropdown(false)
                                        }
                                    }}
                                    className={form.formState.errors.prestazione_id ? 'form-field-error' : ''}
                                    role="combobox"
                                    aria-autocomplete="list"
                                    aria-expanded={showServiceDropdown}
                                    aria-controls="service-options"
                                    aria-activedescendant={highlightedServiceIndex >= 0 ? `service-option-${highlightedServiceIndex}` : undefined}
                                />
                                {showServiceDropdown && (
                                    <div id="service-options" role="listbox" className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                                        {filteredServicesBySearch.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">Nessuna prestazione trovata</div>
                                        ) : (
                                            filteredServicesBySearch.map((service: any, index: number) => (
                                                <button
                                                    id={`service-option-${index}`}
                                                    key={service._id}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={highlightedServiceIndex === index}
                                                    className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 ${highlightedServiceIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onMouseEnter={() => setHighlightedServiceIndex(index)}
                                                    onClick={() => {
                                                        handleServiceChange(service._id, service.nome_visualizzato || service.nome_prestazione)
                                                        setShowServiceDropdown(false)
                                                    }}
                                                >
                                                    {service.nome_visualizzato || service.nome_prestazione}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {form.formState.errors.prestazione_id && (
                                <p className="form-error">{form.formState.errors.prestazione_id.message}</p>
                            )}
                        </div>
                    </div>
                </section>

                {/* Tooth Selection 1*/}
                <Card className="border-0 shadow-none">
                    <CardHeader className="px-0 pb-4 pt-0">
                        <CardTitle className="text-lg">Denti e impianti</CardTitle>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                        {!form.watch('prestazione_id') && (
                            <div className="mb-4 flex items-start gap-2.5 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                <p className="text-sm">
                                    <strong>Manca la prestazione.</strong> Selezionala qui sopra per abilitare denti e impianti.
                                </p>
                            </div>
                        )}
                        <Odontogram
                            companyId={effectiveCompanyIdForPhases}
                            selectedTeeth={selectedTeeth}
                            selectedImplants={selectedImplants}
                            implantTypes={implantTypes}
                            onSelectionChange={handleSelectionChange}
                            disabled={form.formState.isSubmitting || !form.watch('prestazione_id')}
                            pricingMode={singlePricingMode}
                            showValidationErrors={hasAttemptedSubmit}
                            statusId="single-clinical-selection-status"
                        />
                        {canViewPrices && calculatePrice && (
                            <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span>
                                        {calculatePrice.pricingMode === 'per_tooth'
                                            ? `${calculatePrice.billableCount} elementi × ${formatCurrency(calculatePrice.basePrice)}`
                                            : calculatePrice.pricingMode === 'per_mouth'
                                                ? `1 bocca × ${formatCurrency(calculatePrice.basePrice)}`
                                            : `${calculatePrice.billableCount} ${calculatePrice.billableCount === 1 ? 'arcata' : 'arcate'} × ${formatCurrency(calculatePrice.basePrice)}`}
                                    </span>
                                    <strong>Totale {formatCurrency(calculatePrice.finalPrice)}</strong>
                                </div>
                                {calculatePrice.pricingMode === 'per_tooth' &&
                                    calculatePrice.billableCount >= 16 && (
                                        <p className="mt-1 text-xs text-blue-800">
                                            La selezione rapida dell&apos;arcata mantiene un elemento per ogni dente.
                                        </p>
                                    )}
                            </div>
                        )}
                    </CardContent>
                </Card>
                </div>
                </>
                )}

                {prescriptionMode === 'mixed' && (
                    <MixedPrescriptionRows
                        companyId={effectiveCompanyIdForPhases}
                        rows={mixedRows}
                        onChange={setMixedRows}
                        services={services || []}
                        hasAttemptedSubmit={hasAttemptedSubmit}
                    />
                )}
                </div>
                )}

                {currentStep === 3 && (
                    <div className="space-y-5">
                        {prescriptionMode === 'mixed' && (
                            <p className="text-sm text-gray-600">
                                Fase di lavoro, tinta, data/ora e opzioni di consegna sono uniche e
                                vengono applicate a tutti i componenti del gruppo. Solo
                                odontogramma, prestazione e nota sono per singola riga.
                            </p>
                        )}
                        <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label htmlFor="laboratory-phase" className="block text-sm font-medium text-gray-700 mb-1">
                                Fase di lavoro *
                            </label>
                            <Controller
                                name="tipo_prova"
                                control={form.control}
                                render={({ field }) => {
                                    const testsForMixed: Array<{ name?: string; id?: number; display_name?: string }> = sharedTests
                                    const catalogOrConfigured: Array<{
                                        name?: string
                                        id?: number
                                        display_name?: string
                                        skip_policy?: 'blocked' | 'free' | 'approval_required'
                                    }> =
                                        effectivePhases?.phases?.length
                                            ? effectivePhases.phases
                                            : parseLaboratoryTests(selectedService?.laboratory_tests)
                                    const tests: Array<{
                                        name?: string
                                        id?: number
                                        display_name?: string
                                        skip_policy?: 'blocked' | 'free' | 'approval_required'
                                    }> =
                                        prescriptionMode === 'mixed'
                                            ? testsForMixed
                                            : catalogOrConfigured
                                    const noPrestazioniMixed =
                                        prescriptionMode === 'mixed' &&
                                        mixedRows.every((r) => !r.prestazione_id)
                                    const noIntersection =
                                        prescriptionMode === 'mixed' &&
                                        !sharedTestsLoading &&
                                        !noPrestazioniMixed &&
                                        sharedTests.length === 0 &&
                                        mixedRows.some((r) => !!r.prestazione_id)
                                    return (
                                        <>
                                            {prescriptionMode === 'single' &&
                                                (effectivePhases?.source === 'company' || effectivePhases?.source === 'organization') && (
                                                    <p className="mb-1 text-xs text-gray-500">
                                                        {effectivePhases?.source === 'organization'
                                                            ? 'Si applicano le prove e le regole di salto della società.'
                                                            : 'Le regole di salto sono applicate automaticamente dal laboratorio.'}
                                                    </p>
                                                )}
                                            <select
                                                id="laboratory-phase"
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e)
                                                    const name = e.target.value
                                                    if (prescriptionMode === 'mixed') {
                                                        form.setValue('prova_id', name ? 1 : 0)
                                                    } else {
                                                        const selectedTest = catalogOrConfigured.find(
                                                            (c: any) => c.name === name
                                                        )
                                                        form.setValue('prova_id', selectedTest?.id || 0)
                                                    }
                                                }}
                                                disabled={
                                                    prescriptionMode === 'mixed' &&
                                                    (sharedTestsLoading || noPrestazioniMixed || noIntersection)
                                                }
                                                className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                                                    hasAttemptedSubmit && form.formState.errors.tipo_prova ? 'border-red-500' : ''
                                                }`}
                                            >
                                                <option value="">
                                                    {prescriptionMode === 'mixed'
                                                        ? sharedTestsLoading
                                                            ? 'Caricamento fasi di lavoro…'
                                                            : noPrestazioniMixed
                                                                ? 'Seleziona prima le prestazioni dei componenti'
                                                                : 'Seleziona fase di lavoro (valida per tutti i componenti)'
                                                        : 'Seleziona fase di lavoro'}
                                                </option>
                                                {tests.map((test, index) => {
                                                    const skipped = prescriptionMode === 'single'
                                                        ? catalogOrConfigured.slice(0, index)
                                                        : []
                                                    const blocked = skipped.some(
                                                        (phase) => phase.skip_policy === 'blocked'
                                                    )
                                                    const approval = skipped.some(
                                                        (phase) => phase.skip_policy === 'approval_required'
                                                    )
                                                    return (
                                                        <option
                                                            key={index}
                                                            value={test.name || ''}
                                                            disabled={blocked}
                                                        >
                                                            {test.name
                                                                ? laboratoryTestLabel({
                                                                    name: test.name,
                                                                    display_name: test.display_name,
                                                                })
                                                                : test.id || ''}
                                                            {blocked
                                                                ? ' — salto non consentito'
                                                                : approval
                                                                    ? ' — richiede approvazione'
                                                                    : ''}
                                                        </option>
                                                    )
                                                })}
                                            </select>
                                            {noIntersection && (
                                                <p className="form-error mt-1">
                                                    Le prestazioni scelte non hanno fasi in comune.
                                                    Modifica le prestazioni o usa prescrizioni separate.
                                                </p>
                                            )}
                                        </>
                                    )
                                }}
                            />
                            {hasAttemptedSubmit && form.formState.errors.tipo_prova && (
                                <p className="form-error">{form.formState.errors.tipo_prova.message}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="laboratory-tint" className="block text-sm font-medium text-gray-700 mb-1">
                                Tinta *
                            </label>
                            <Controller
                                name="tinta"
                                control={form.control}
                                render={({ field }) => (
                                    <select
                                        id="laboratory-tint"
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e); // aggiorna 'tinta'
                                            const v = e.target.value
                                            const selectedColor = colors?.find(
                                                (c: { code?: unknown; id?: unknown }) =>
                                                    (c.code != null && String(c.code) === String(v)) ||
                                                    (c.id != null && String(c.id) === String(v))
                                            )
                                            form.setValue('color_id', selectedColor?.id || 0)
                                        }}
                                        className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                                            hasAttemptedSubmit && form.formState.errors.tinta ? 'border-red-500' : ''
                                        }`}
                                        disabled={colorsLoading}
                                    >
                                        <option value="">
                                            {colorsLoading ? 'Caricamento tinte...' : 'Seleziona tinta'}
                                        </option>
                                        {colors?.map((color, index) => (
                                            <option key={index} value={color.code || color.id || color}>
                                                {color.code || color.id || color}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                            {hasAttemptedSubmit && form.formState.errors.tinta && (
                                <p className="form-error">{form.formState.errors.tinta.message}</p>
                            )}
                        </div>
                        </div>

                        <input type="hidden" {...form.register('color_id')} />


                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="delivery-date" className="block text-sm font-medium text-gray-700 mb-1">
                                    Data *
                                </label>
                                <Input
                                    id="delivery-date"
                                    type="date"
                                    {...form.register('dataapp')}
                                    min={minDateString}
                                    className={hasAttemptedSubmit && form.formState.errors.dataapp ? 'border-red-500' : ''}
                                />
                                {hasAttemptedSubmit && form.formState.errors.dataapp && (
                                    <p className="form-error">{form.formState.errors.dataapp.message}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="delivery-time" className="block text-sm font-medium text-gray-700 mb-1">
                                    Ora *
                                </label>
                                <Input
                                    id="delivery-time"
                                    type="time"
                                    {...form.register('timeapp')}
                                    min={form.watch('dataapp') === minDateString ? minTimeString : undefined}
                                    className={hasAttemptedSubmit && form.formState.errors.timeapp ? 'border-red-500' : ''}
                                />
                                {hasAttemptedSubmit && form.formState.errors.timeapp && (
                                    <p className="form-error">{form.formState.errors.timeapp.message}</p>
                                )}
                            </div>
                        </div>

                        <fieldset className="space-y-3 rounded-lg border border-gray-200 p-4">
                            <legend className="px-1 text-sm font-semibold text-gray-800">
                                Modalità impronta *
                            </legend>
                            <p className="text-xs text-gray-500">
                                Le due modalità sono alternative: scegli digitale oppure analogica.
                            </p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <label
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                                        form.watch('scansione_intraorale')
                                            ? 'border-primoup-500 bg-primoup-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="delivery_mode"
                                        checked={form.watch('scansione_intraorale')}
                                        onChange={() => {
                                            form.setValue('scansione_intraorale', true, { shouldValidate: true })
                                            form.setValue('presa_fisica_corriere', false, { shouldValidate: true })
                                        }}
                                        className="mt-0.5 border-gray-300 text-primoup-600 focus:ring-primoup-500"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-800">Digitale</span>
                                        <span className="block text-xs text-gray-500">Scansione intraorale</span>
                                    </span>
                                </label>
                                <label
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                                        form.watch('presa_fisica_corriere')
                                            ? 'border-primoup-500 bg-primoup-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="delivery_mode"
                                        checked={form.watch('presa_fisica_corriere')}
                                        onChange={() => {
                                            form.setValue('presa_fisica_corriere', true, { shouldValidate: true })
                                            form.setValue('scansione_intraorale', false, { shouldValidate: true })
                                        }}
                                        className="mt-0.5 border-gray-300 text-primoup-600 focus:ring-primoup-500"
                                    />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-800">Analogica</span>
                                        <span className="block text-xs text-gray-500">Presa fisica con avviso al laboratorio</span>
                                    </span>
                                </label>
                            </div>
                            {hasAttemptedSubmit && form.formState.errors.scansione_intraorale && (
                                <p className="form-error">{form.formState.errors.scansione_intraorale.message}</p>
                            )}

                            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
                                <input
                                    type="checkbox"
                                    id="chiama_medico"
                                    {...form.register('chiama_medico')}
                                    className="mt-0.5 rounded border-gray-300 text-primoup-600 focus:ring-primoup-500"
                                />
                                <span>
                                    <span className="block text-sm font-medium text-gray-800">Chiama il medico</span>
                                    <span className="block text-xs text-gray-500">
                                        Può essere associato a entrambe le modalità e genera un avviso CONTATTA-MEDICO.
                                    </span>
                                </span>
                            </label>
                        </fieldset>

                        <div>
                            <span className="block text-sm font-medium text-gray-700 mb-1">
                                Allegati
                            </span>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <input
                                    id="prescription-files"
                                    type="file"
                                    multiple
                                    accept={PRIMOUP_ATTACHMENT_ACCEPT}
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || [])
                                        const validFiles = files.filter((file) =>
                                            !getPrimoUpAttachmentValidationError({
                                                filename: file.name,
                                                contentType: file.type,
                                            })
                                        )
                                        const invalidFiles = files.filter((file) => !validFiles.includes(file))
                                        setSelectedFiles(validFiles)
                                        if (invalidFiles.length > 0) {
                                            showFlowError(
                                                3,
                                                'Allegato non supportato',
                                                `Puoi caricare soltanto immagini JPG, JPEG o PNG. File esclusi: ${invalidFiles.map((file) => file.name).join(', ')}.`
                                            )
                                        }
                                        e.target.value = ''
                                    }}
                                    className="peer sr-only"
                                    disabled={uploadingFiles}
                                />
                                <label
                                    htmlFor="prescription-files"
                                    className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 peer-focus-visible:outline-hidden peer-focus-visible:ring-2 peer-focus-visible:ring-primoup-500 peer-focus-visible:ring-offset-2"
                                >
                                    <Paperclip className="h-4 w-4" aria-hidden="true" />
                                    Scegli allegati
                                </label>
                                <span className="text-xs text-gray-500">
                                    {selectedFiles.length > 0
                                        ? `${selectedFiles.length} file selezionati`
                                        : 'Nessun file selezionato'}
                                    {uploadingFiles && ' · Caricamento in corso...'}
                                </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Formati supportati da PrimoUp: JPG, JPEG e PNG.
                            </p>
                            {selectedFiles.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="text-xs text-gray-600 flex items-center justify-between">
                                            <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                                            <button
                                                type="button"
                                                aria-label={`Rimuovi file ${file.name}`}
                                                title={`Rimuovi ${file.name}`}
                                                onClick={() => {
                                                    const newFiles = selectedFiles.filter((_, i) => i !== index);
                                                    setSelectedFiles(newFiles);
                                                }}
                                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                                                disabled={uploadingFiles}
                                            >
                                                <X className="h-4 w-4" aria-hidden="true" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <label htmlFor="laboratory-notes" className="block text-sm font-medium text-gray-700 mb-1">
                                {prescriptionMode === 'mixed' ? 'Note di gruppo' : 'Note'}
                            </label>
                            <textarea
                                id="laboratory-notes"
                                {...form.register('note')}
                                rows={3}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                placeholder={
                                    prescriptionMode === 'mixed'
                                        ? 'Note condivise tra i componenti (opzionale)'
                                        : 'Note aggiuntive per la prescrizione'
                                }
                            />
                        </div>
                    </div>
                )}

                {currentStep === 4 && (
                    <div className="space-y-6">
                        <section aria-labelledby="review-patient-heading">
                            <div className="flex items-center justify-between gap-4">
                                <h3 id="review-patient-heading" className="text-base font-semibold text-gray-950">Paziente</h3>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentStep(1)}>Modifica</Button>
                            </div>
                            <dl className="mt-3 grid gap-x-6 gap-y-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2">
                                <div><dt className="text-gray-500">Nome paziente</dt><dd className="mt-1 font-medium text-gray-900">{watchedValues.NomePaziente || 'Non inserito'}</dd></div>
                                <div><dt className="text-gray-500">Medico</dt><dd className="mt-1 font-medium text-gray-900">{doctorForSummary ? `${doctorForSummary.nome} ${doctorForSummary.cognome}` : 'Non selezionato'}</dd></div>
                                {isLabOperator && <div><dt className="text-gray-500">Clinica</dt><dd className="mt-1 font-medium text-gray-900">{selectedCompany?.nome || 'Non selezionata'}</dd></div>}
                            </dl>
                        </section>

                        <section aria-labelledby="review-configuration-heading" className="border-t border-gray-100 pt-6">
                            <div className="flex items-center justify-between gap-4">
                                <h3 id="review-configuration-heading" className="text-base font-semibold text-gray-950">Configurazione clinica</h3>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentStep(2)}>Modifica</Button>
                            </div>
                            <dl className="mt-3 grid gap-x-6 gap-y-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2">
                                <div><dt className="text-gray-500">Modalità</dt><dd className="mt-1 font-medium text-gray-900">{prescriptionMode === 'single' ? 'Singola' : 'Mista'}</dd></div>
                                {prescriptionMode === 'single' ? (
                                    <>
                                        <div><dt className="text-gray-500">Prestazione</dt><dd className="mt-1 font-medium text-gray-900">{selectedServiceLabel || 'Non selezionata'}</dd></div>
                                        <div><dt className="text-gray-500">Elementi</dt><dd className="mt-1 font-medium text-gray-900">{selectedElementLabel}</dd></div>
                                    </>
                                ) : <div><dt className="text-gray-500">Componenti</dt><dd className="mt-1 font-medium text-gray-900">{mixedRows.length}</dd></div>}
                            </dl>
                        </section>

                        <section aria-labelledby="review-details-heading" className="border-t border-gray-100 pt-6">
                            <div className="flex items-center justify-between gap-4">
                                <h3 id="review-details-heading" className="text-base font-semibold text-gray-950">Dettagli laboratorio</h3>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentStep(3)}>Modifica</Button>
                            </div>
                            <dl className="mt-3 grid gap-x-6 gap-y-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2">
                                <div><dt className="text-gray-500">Fase di lavoro</dt><dd className="mt-1 font-medium text-gray-900">{watchedValues.tipo_prova || 'Non selezionata'}</dd></div>
                                <div><dt className="text-gray-500">Tinta</dt><dd className="mt-1 font-medium text-gray-900">{watchedValues.tinta || 'Non selezionata'}</dd></div>
                                <div><dt className="text-gray-500">Appuntamento</dt><dd className="mt-1 font-medium text-gray-900">{appointmentSummary}</dd></div>
                                <div><dt className="text-gray-500">Modalità impronta</dt><dd className="mt-1 font-medium text-gray-900">{impressionMode}</dd></div>
                                <div><dt className="text-gray-500">Allegati</dt><dd className="mt-1 font-medium text-gray-900">{selectedFiles.length}</dd></div>
                                {canViewPrices && prescriptionMode === 'single' && calculatePrice && <div><dt className="text-gray-500">Totale</dt><dd className="mt-1 font-semibold tabular-nums text-gray-950">{formatCurrency(calculatePrice.finalPrice)}</dd></div>}
                            </dl>
                        </section>
                    </div>
                )}

                        </div>
                        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                            <div className="flex gap-2">
                                {onCancel && (
                                    <Button type="button" variant="ghost" onClick={onCancel} disabled={form.formState.isSubmitting}>Annulla</Button>
                                )}
                                {currentStep > 1 && (
                                    <Button type="button" variant="outline" onClick={() => setCurrentStep((step) => step - 1)}>
                                        <ChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Indietro
                                    </Button>
                                )}
                            </div>

                            {currentStep < 4 ? (
                        <Button
                            type="button"
                            variant="primoup"
                            onClick={() => void goToNextStep()}
                        >
                            Continua
                            <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
                        </Button>
                            ) : prescriptionMode === 'single' ? (
                    <Button
                        type="submit"
                        variant="primoup"
                        disabled={form.formState.isSubmitting}
                        onClick={() => {
                            setIsIntentionalSubmit(true)
                            setHasAttemptedSubmit(true)
                        }}
                    >
                        {form.formState.isSubmitting ? 'Creazione...' : 'Crea prescrizione'}
                    </Button>
                    ) : (
                    <Button
                        type="button"
                        variant="primoup"
                        disabled={mixedSubmitting}
                        onClick={() => void handleMixedSubmit()}
                    >
                        {mixedSubmitting ? 'Invio in corso…' : 'Crea prescrizione mista'}
                    </Button>
                    )}
                        </div>
                    </div>

                    <aside className="rounded-xl border border-gray-200 bg-white xl:sticky xl:top-4" aria-labelledby="prescription-summary-title">
                        <div className="border-b border-gray-100 px-5 py-5">
                            <h2 id="prescription-summary-title" className="text-base font-semibold text-gray-950">Riepilogo prescrizione</h2>
                            <p className="mt-1 text-sm text-gray-500">Si aggiorna con i dati già inseriti.</p>
                        </div>
                        <div className="space-y-5 px-5 py-5">
                            <dl className="space-y-3 text-sm">
                                <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Paziente</dt><dd className="max-w-[58%] text-right font-medium text-gray-900">{watchedValues.NomePaziente || '—'}</dd></div>
                                <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Medico</dt><dd className="max-w-[58%] text-right font-medium text-gray-900">{doctorForSummary ? `${doctorForSummary.nome} ${doctorForSummary.cognome}` : '—'}</dd></div>
                                <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Modalità</dt><dd className="text-right font-medium text-gray-900">{prescriptionMode === 'single' ? 'Singola' : 'Mista'}</dd></div>
                            </dl>
                            <div className="border-t border-gray-100 pt-5">
                                <h3 className="text-sm font-semibold text-gray-900">Dati clinici</h3>
                                <dl className="mt-3 space-y-3 text-sm">
                                    {prescriptionMode === 'single' ? (
                                        <>
                                            <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Prestazione</dt><dd className="max-w-[58%] text-right font-medium text-gray-900">{selectedServiceLabel || '—'}</dd></div>
                                            <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Elementi</dt><dd className="max-w-[58%] text-right font-medium text-gray-900">{selectedElementLabel}</dd></div>
                                        </>
                                    ) : <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Componenti</dt><dd className="font-medium tabular-nums text-gray-900">{mixedRows.length}</dd></div>}
                                    <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Fase</dt><dd className="max-w-[58%] text-right font-medium text-gray-900">{watchedValues.tipo_prova || '—'}</dd></div>
                                    <div className="flex items-start justify-between gap-4"><dt className="text-gray-500">Tinta</dt><dd className="font-medium text-gray-900">{watchedValues.tinta || '—'}</dd></div>
                                </dl>
                            </div>
                            <div className="border-t border-gray-100 pt-5">
                                <h3 className="text-sm font-semibold text-gray-900">Completamento</h3>
                                <ol className="mt-3 space-y-3">
                                    {workflowSteps.map((step) => {
                                        const isActive = currentStep === step.number
                                        const isComplete = currentStep > step.number || step.complete
                                        return (
                                            <li key={step.number} className="flex items-center gap-3 text-sm">
                                                <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold ${isComplete ? 'border-secondary bg-secondary text-white' : isActive ? 'border-primary bg-primary text-white' : 'border-gray-300 text-gray-500'}`}>
                                                    {isComplete ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : step.number}
                                                </span>
                                                <span className={isActive ? 'font-medium text-gray-950' : 'text-gray-600'}>{step.label}</span>
                                            </li>
                                        )
                                    })}
                                </ol>
                            </div>
                        </div>
                    </aside>
                </div>
            </form>
        </LoadingOverlay>
    )
}
